import { useNotifications } from 'modules/app/notifications';
import { DangerConfirmationModal } from 'src/core/common/components/DangerConfirmationModal';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { type SaveChanges } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import { type AnalyticalFieldMappingV2 } from 'src/core/modules/bookkeep';

import { useDeleteEcQuery } from './hooks/useDeleteEcQuery';

type Props = {
  expenseCategory: { id: string; name: string };
  isOpen: boolean;
  onComplete(): void;
  onClose(): void;
  saveMappingChanges(mappingChanges: SaveChanges): void;
  isMapSyncEnabled?: boolean;
  fieldMapping?: AnalyticalFieldMappingV2;
  syncMode?: SyncMode;
};

export const EcDeletionModalContainer = ({
  expenseCategory,
  isOpen,
  onComplete,
  onClose,
  saveMappingChanges,
  fieldMapping,
  isMapSyncEnabled,
  syncMode,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const isBudgetsFeatureEnabled = useFeature(FEATURES.BUDGETS);
  let description = isBudgetsFeatureEnabled
    ? t('expenseCategories.deletion.modalSubtitleWithBudgetFeature', {
        name: expenseCategory.name,
      })
    : t('expenseCategories.deletion.modalSubtitleWithoutBudgetFeature', {
        name: expenseCategory.name,
      });
  const { dangerNotif, successNotif } = useNotifications();
  const [handleOnSubmit] = useDeleteEcQuery(expenseCategory.id);

  const isValueMapped =
    syncMode === 'pullAndMap' &&
    fieldMapping?.values.some(
      (value) => value.internalId === expenseCategory.id,
    );

  if (isValueMapped) {
    description = t(
      'bookkeep.integrations.settings.analyticalFieldMapping.deleteExpenseCategoryValue',
      { integration: accountingSoftwareName },
    );
  }

  return (
    <DangerConfirmationModal
      isOpen={isOpen}
      title={t('expenseCategories.deletion.modalTitle')}
      description={description}
      actionText={t('expenseCategories.deletion.delete')}
      withRejectionMessage={false}
      onClose={onClose}
      onConfirm={async () => {
        try {
          await handleOnSubmit(expenseCategory);
          successNotif(t('expenseCategories.deletion.success'));

          if (isMapSyncEnabled && fieldMapping?.internalId) {
            saveMappingChanges({
              mapping: {
                fieldId: fieldMapping.id,
                fieldType: fieldMapping.fieldType,
                internalId: fieldMapping.internalId,
                values: fieldMapping.values.map((value) => {
                  if (value.internalId === expenseCategory.id) {
                    return {
                      ...value,
                      internalId: null,
                    };
                  }
                  return value;
                }),
              },
            });
          }

          onComplete();
        } catch {
          dangerNotif(t('expenseCategories.deletion.error'));
        }
      }}
    />
  );
};
